import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Backdrop from "@mui/material/Backdrop";
import FamilyHeader from "./FamilyHeader";
import FamilyAccountNotFoundDialog from "./FamilyAccountNotFoundDialog";
import ContactVerificationPrompt from "../../common/components/ContactVerificationPrompt";
import { CONTACT_VERIFICATION_ROLE, markContactVerificationPromptShown } from "../../common/utils/contactVerification";
import { resolveHomeVerificationPrompt } from "../../common/utils/homeVerificationPrompt";
import { getSessionContext } from "../../common/utils/sessionContext";
import { persistFamilyClientSelection, performSessionLogout } from "../../common/utils/authSession";
import { getClientsByFamilyMember } from "../../common/api/familyportalOtpApi";
import { FaClipboardList, FaHeartbeat, FaUser, FaUsers } from "react-icons/fa";
import { HiSparkles } from "react-icons/hi2";
import * as Sentry from "@sentry/react";

const HOME_SELECTED_CLIENT_KEY = "familyHomeSelectedClientId";

const FamilyHome = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { familyMemberId, familyMemberName } = getSessionContext();
  const greetingName = familyMemberName?.trim() || "there";
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(() => sessionStorage.getItem(HOME_SELECTED_CLIENT_KEY) || "");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [accountNotFound, setAccountNotFound] = useState(false);
  const [showVerificationPrompt, setShowVerificationPrompt] = useState(false);
  const [verificationPromptMessage, setVerificationPromptMessage] = useState("");

  const handleAccountNotFoundLogout = async () => {
    const isNativeWebView = Boolean(window.flutter_inappwebview?.callHandler);
    await performSessionLogout({ navigate: isNativeWebView ? undefined : navigate });
  };

  useEffect(() => {
    let cancelled = false;

    const loadVerificationPrompt = async () => {
      try {
        const result = await resolveHomeVerificationPrompt(CONTACT_VERIFICATION_ROLE.FAMILY);
        if (cancelled || !result.shouldShow) {
          return;
        }

        setVerificationPromptMessage(result.message);
        setShowVerificationPrompt(true);
        markContactVerificationPromptShown(CONTACT_VERIFICATION_ROLE.FAMILY);
      } catch (err) {
        Sentry.captureException(err);
      }
    };

    loadVerificationPrompt();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchClients = async () => {
      setLoading(true);
      setError("");

      try {
        if (Array.isArray(location.state?.clients)) {
          if (!cancelled) {
            setClients(location.state.clients);
            setLoading(false);
          }
          return;
        }

        if (!familyMemberId) {
          if (!cancelled) {
            setClients([]);
            setError("Please sign in to view clients.");
            setLoading(false);
          }
          return;
        }

        const result = await getClientsByFamilyMember(familyMemberId);
        if (cancelled) {
          return;
        }

        if (!result.success || !Array.isArray(result.data)) {
          setClients([]);
          if (result.accountNotFound) {
            setAccountNotFound(true);
          } else {
            setError(result.message || "Failed to load clients.");
          }
          return;
        }

        setClients(result.data);
      } catch (err) {
        Sentry.captureException(err);
        if (!cancelled) {
          setClients([]);
          setError("Failed to load clients.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchClients();
    return () => {
      cancelled = true;
    };
  }, [familyMemberId, location.state?.clients]);

  useEffect(() => {
    if (!clients.length || !selectedClientId) {
      return;
    }

    const stillExists = clients.some((client) => String(client.clientId) === String(selectedClientId));
    if (!stillExists) {
      setSelectedClientId("");
      sessionStorage.removeItem(HOME_SELECTED_CLIENT_KEY);
    }
  }, [clients, selectedClientId]);

  const handleCloseVerificationPrompt = () => {
    setShowVerificationPrompt(false);
    markContactVerificationPromptShown(CONTACT_VERIFICATION_ROLE.FAMILY);
  };

  const handleSelectClient = (client) => {
    if (!client?.clientId) {
      return;
    }

    persistFamilyClientSelection(client);
    sessionStorage.setItem(HOME_SELECTED_CLIENT_KEY, String(client.clientId));
    setSelectedClientId(client.clientId);
  };

  useEffect(() => {
    if (clients.length !== 1) {
      return;
    }

    const onlyClient = clients[0];
    if (String(selectedClientId) !== String(onlyClient.clientId)) {
      handleSelectClient(onlyClient);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients]);

  const menus = [
    {
      label: "Checklist",
      path: "/family-checklist",
      icon: FaClipboardList,
      description: "Daily care tasks & completion status",
    },
    {
      label: "Vitals",
      path: "/family-vitals",
      icon: FaHeartbeat,
      description: "Blood pressure & health readings",
    },
  ];

  const isSingleClient = clients.length === 1;
  const effectiveSelectedClientId = isSingleClient ? clients[0].clientId : selectedClientId;
  const hasSelection = Boolean(effectiveSelectedClientId);
  const selectedClient = clients.find((client) => String(client.clientId) === String(effectiveSelectedClientId));
  const showClientPicker = !loading && !error && !accountNotFound && clients.length > 1;

  return (
    <div className="family-home-layout">
      <FamilyHeader clientName={hasSelection ? selectedClient?.clientName || "" : ""} restrictMenu={accountNotFound} />
      <Backdrop sx={{ color: "#ffba30", zIndex: (theme) => theme.zIndex.drawer + 1000 }} open={loading}>
        <img src={require("../../assets/btTree.png")} className="loading-img" alt="" />
      </Backdrop>

      <main className="family-home-page">
        <section className="family-home-hero">
          <HiSparkles className="family-home-hero-star" aria-hidden="true" />
          <h1 className="family-home-hero-title">Hi, {greetingName}</h1>
          <div className="family-home-hero-copy">
            <p className="family-home-hero-line">
              {isSingleClient ? "Here's what's happening with your loved one's care right now." : "Select a family member to view their care information."}
            </p>
          </div>

          {!loading && error && !accountNotFound ? <p className="family-clients-home-error">{error}</p> : null}

          {!loading && !error && !accountNotFound && clients.length === 0 ? (
            <p className="family-clients-home-empty" role="status">
              No Family Members has been mapped to you. Please contact administrator
            </p>
          ) : null}

          {showClientPicker ? (
            <div className="family-clients-home-grid" role="list">
              {clients.map((client) => {
                const isActive = String(effectiveSelectedClientId) === String(client.clientId);
                return (
                  <button key={client.clientId} type="button" role="listitem" className={`family-clients-home-client-card${isActive ? " is-active" : ""}`} onClick={() => handleSelectClient(client)} aria-pressed={isActive}>
                    <span className="family-clients-home-client-icon" aria-hidden="true">
                      <FaUser />
                    </span>
                    <span className="family-clients-home-client-name">{client.clientName}</span>
                  </button>
                );
              })}
            </div>
          ) : null}

          {!isSingleClient && hasSelection ? <p className="family-home-hero-line family-clients-home-hint">Choose what you would like to view today.</p> : null}
        </section>

        {hasSelection ? (
          <div className="family-home-cards">
            {menus.map(({ label, path, icon: Icon, description }) => (
              <button key={label} type="button" className="family-home-card" onClick={() => navigate(path)}>
                <span className="family-home-card-icon-wrap">
                  <Icon className="family-home-card-icon" aria-hidden="true" />
                </span>
                <span className="family-home-card-body">
                  <span className="family-home-card-title">{label}</span>
                  <span className="family-home-card-desc">{description}</span>
                </span>
              </button>
            ))}
          </div>
        ) : null}

        {showClientPicker && !hasSelection ? (
          <div className="family-clients-home-placeholder" role="status">
            <span className="family-clients-home-placeholder-icon" aria-hidden="true">
              <FaUsers />
            </span>
            <p className="family-clients-home-placeholder-title">No Family Member Selected Yet</p>
            <p className="family-clients-home-placeholder-copy">Select a family member above to view their checklist and vitals</p>
          </div>
        ) : null}
      </main>

      <ContactVerificationPrompt open={showVerificationPrompt} onClose={handleCloseVerificationPrompt} role={CONTACT_VERIFICATION_ROLE.FAMILY} message={verificationPromptMessage} />
      <FamilyAccountNotFoundDialog open={accountNotFound} onLogout={handleAccountNotFoundLogout} />
    </div>
  );
};

export default FamilyHome;
